import { useState, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useAI } from '@/context/AIContext';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Loader2, Brain, FileText, Lightbulb, AlertTriangle, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export function MeetingPrepButton({ clientId, clientName }) {
  const { token } = useAuth();
  const { aiEnabled } = useAI();
  const [loading, setLoading] = useState(false);
  const [brief, setBrief] = useState(null);
  const [open, setOpen] = useState(false);

  const generateBrief = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE}/api/ai-insights/meeting-prep/${clientId}`,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 }
      );
      setBrief(res.data);
      setOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate brief');
    } finally {
      setLoading(false);
    }
  }, [clientId, token]);

  if (!aiEnabled) return null;

  return (
    <>
      <Button
        onClick={generateBrief}
        disabled={loading}
        variant="outline"
        className="gap-2 text-[#4A3B32] border-[#4A3B32]/30 hover:bg-[#4A3B32]/5"
        data-testid="meeting-prep-btn"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Brain className="w-4 h-4" />}
        {loading ? 'Generating...' : 'AI Meeting Prep'}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="meeting-prep-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-[#4A3B32]" />
              Meeting Prep — {brief?.clientName}
            </DialogTitle>
            <DialogDescription>
              AI-generated brief based on {brief?.dataSnapshot?.activitiesCount || 0} recent activities,{' '}
              {brief?.dataSnapshot?.overdueTasksCount || 0} overdue tasks, and{' '}
              {brief?.dataSnapshot?.upcomingTasksCount || 0} upcoming tasks
            </DialogDescription>
          </DialogHeader>
          <div className="prose prose-sm max-w-none mt-4 text-[#2C2420]"
            data-testid="meeting-prep-content"
            dangerouslySetInnerHTML={{
              __html: brief?.brief?.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                .replace(/\n- /g, '\n<li>')
                .replace(/\n(\d+)\. /g, '\n<li>')
                .replace(/\n\n/g, '<br/><br/>')
                .replace(/\n/g, '<br/>') || ''
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

export function TaskSuggestionsButton({ clientId, clientName, onCreateTask }) {
  const { token } = useAuth();
  const { aiEnabled } = useAI();
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [open, setOpen] = useState(false);

  const generateSuggestions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE}/api/ai-insights/task-suggestions/${clientId}`,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 }
      );
      setSuggestions(res.data);
      setOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate suggestions');
    } finally {
      setLoading(false);
    }
  }, [clientId, token]);

  const priorityColors = {
    High: 'bg-red-100 text-red-700',
    Medium: 'bg-amber-100 text-amber-700',
    Low: 'bg-green-100 text-green-700',
  };

  if (!aiEnabled) return null;

  return (
    <>
      <Button
        onClick={generateSuggestions}
        disabled={loading}
        variant="outline"
        className="gap-2 text-[#4A3B32] border-[#4A3B32]/30 hover:bg-[#4A3B32]/5"
        data-testid="task-suggestions-btn"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lightbulb className="w-4 h-4" />}
        {loading ? 'Thinking...' : 'AI Task Suggestions'}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="task-suggestions-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-amber-500" />
              Smart Task Suggestions — {suggestions?.clientName}
            </DialogTitle>
            <DialogDescription>
              AI-recommended tasks based on client profile and existing tasks
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {suggestions?.suggestions?.map((s, i) => (
              <div key={i} className="p-3 rounded-lg border border-[#E6E0D9] hover:border-[#4A3B32]/30 transition-colors"
                data-testid={`suggestion-${i}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#2C2420]">{s.title}</p>
                    <p className="text-xs text-[#8C8078] mt-1">{s.reason}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={cn("text-[10px] px-1.5 py-0.5 rounded-full", priorityColors[s.priority] || priorityColors.Medium)}>
                        {s.priority}
                      </span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#4A3B32]/10 text-[#4A3B32]">
                        {s.category}
                      </span>
                      <span className="text-[10px] text-[#8C8078]">
                        Due in {s.dueInDays}d
                      </span>
                    </div>
                  </div>
                  {onCreateTask && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0"
                      onClick={() => { onCreateTask(s); toast.success(`Task "${s.title}" creation initiated`); }}
                      data-testid={`create-suggestion-${i}`}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function AnomalyAlerts() {
  const { token } = useAuth();
  const { aiEnabled } = useAI();
  const [loading, setLoading] = useState(false);
  const [anomalies, setAnomalies] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const detect = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE}/api/ai-insights/anomaly-detection`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAnomalies(res.data);
      setExpanded(true);
    } catch (err) {
      toast.error('Failed to run anomaly detection');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const severityIcon = {
    high: <AlertTriangle className="w-4 h-4 text-red-500" />,
    medium: <AlertTriangle className="w-4 h-4 text-amber-500" />,
    low: <AlertTriangle className="w-4 h-4 text-blue-500" />,
  };

  const severityBg = {
    high: 'border-l-red-500 bg-red-50/50',
    medium: 'border-l-amber-500 bg-amber-50/50',
    low: 'border-l-blue-500 bg-blue-50/50',
  };

  if (!aiEnabled) return null;

  if (!anomalies && !loading) {
    return (
      <Button onClick={detect} variant="outline" className="gap-2 w-full"
        data-testid="anomaly-detect-btn">
        <AlertTriangle className="w-4 h-4" />
        Run Anomaly Detection
      </Button>
    );
  }

  return (
    <div className="space-y-3" data-testid="anomaly-alerts">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-[#4A3B32]" />
          <span className="text-sm font-medium text-[#2C2420]">
            {loading ? 'Scanning...' : `${anomalies?.anomalies?.length || 0} Issues Found`}
          </span>
          {anomalies && (
            <span className="text-xs text-[#8C8078]">
              ({anomalies.clientsWithIssues} of {anomalies.totalClients} clients)
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={detect} disabled={loading}
            className="text-xs h-7" data-testid="anomaly-refresh-btn">
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Refresh'}
          </Button>
          {anomalies && (
            <Button variant="ghost" size="icon" className="h-7 w-7"
              onClick={() => setExpanded(!expanded)}>
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          )}
        </div>
      </div>

      {expanded && anomalies?.anomalies && (
        <div className="space-y-2 max-h-[300px] overflow-y-auto">
          {anomalies.anomalies.map((a, i) => (
            <div key={i} className={cn("flex items-start gap-3 p-3 rounded-lg border-l-4", severityBg[a.severity])}
              data-testid={`anomaly-${i}`}>
              {severityIcon[a.severity]}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-[#2C2420]">{a.clientName}</p>
                <p className="text-xs text-[#5D524D]">{a.message}</p>
              </div>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white text-[#8C8078] shrink-0 capitalize">
                {a.type.replace(/_/g, ' ')}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
